import Edit from "@mui/icons-material/Edit"
import {
    TextField,
    Stack,
    Autocomplete,
    CircularProgress,
    Collapse,
    Typography,
} from "@mui/material"
import match from "autosuggest-highlight/match"
import parse from "autosuggest-highlight/parse"
import { ReactElement, SyntheticEvent, useState } from "react"
import { Control, FieldValues, UseFormSetValue } from "react-hook-form"

import { UK_POSTCODE_REGEX } from "../../../../constants/patterns"
import {
    TT_ADDRESS_SEARCH,
    TT_ADDRESS_SEARCH_MANUAL,
    TT_AMENDMENT_MODE_DISABLED,
    TT_POSTCODE_INPUT,
} from "../../../../constants/tooltips"
import { useScreenSize } from "../../../../hooks/useScreenSize"
import useAddress from "../../../../services/middleware/hooks/singleApplication/useAddress"
import usePlaceID from "../../../../services/middleware/hooks/singleApplication/useGetPlaceId"
import { ITEM_BORDER_MULTIPLIER } from "../../../../themes/defaultThemeParams"
import { noOptionsText } from "../../../../utils/general/generalUtils"
import AppButton from "../../../buttons/appButton"
import HelpIcon from "../../../helper/helpIcon"
import ValidatedTextField from "../../../inputs/validated/validatedTextField"
import { APPLICATION_SPACING } from "../formGenerator/formDispatcher"

export const ADDRESS_KW = "mainAddress"
export const POSTCODE_KW = "installationPostCode"

export const setAddressFields = (
    setValue: UseFormSetValue<FieldValues>,
    address: string | undefined,
    postalCode: string | undefined
): void => {
    setValue(ADDRESS_KW, address)
    setValue(POSTCODE_KW, postalCode)
}

interface AddressFormProps {
    setValue: UseFormSetValue<FieldValues>
    control: Control
    isDefaults: boolean
    label: string
    worldwide?: boolean
    disabled?: boolean
}

/**
 * AddressForm component for displaying and managing address form fields.
 * Note the address and postcode fields are deliberately mounted in the
 * `Collapse` to ensure they are caught but react-hook-form when the form is
 * submitted.
 */
const AddressForm = ({
    control,
    setValue,
    isDefaults,
    label,
    worldwide = false,
    disabled = false,
}: AddressFormProps): ReactElement => {
    const [searchTerm, setSearchTerm] = useState("")
    const { isMob } = useScreenSize()
    const { addressId, isLoading: searchAddressLoading } = useAddress(
        searchTerm,
        worldwide
    )
    const { getAddressWiD, isLoading: addressLoading } = usePlaceID(setValue)
    const [hideAddressFields, setHideAddressFields] = useState(isDefaults)
    const [showButton, setShowButton] = useState(!isDefaults)

    // called when user types in the address search field to fetch suggestions
    const handleSearch = (
        _event: SyntheticEvent<Element, Event>,
        value: string
    ): void => {
        if (value) {
            setSearchTerm(value)
        }
    }

    // called when user selects an address from the autocomplete suggestions
    const handleSelect = (
        _event: SyntheticEvent<Element, Event>,
        value: { PlaceId: string; Text: string } | null
    ): void => {
        if (value !== null) {
            getAddressWiD(value.PlaceId)
        } else {
            setAddressFields(setValue, undefined, undefined)
        }
        setHideAddressFields(true)
        setShowButton(false)
    }

    const displayAddressFields = (): void => {
        setHideAddressFields(true)
        setShowButton(false)
    }

    return (
        <>
            <Stack spacing={4} direction="row">
                <Autocomplete
                    fullWidth
                    options={addressId ?? []}
                    loading={searchAddressLoading}
                    isOptionEqualToValue={(option, value) =>
                        option.Text === value.Text
                    }
                    getOptionLabel={(option) => String(option.Text)}
                    onChange={handleSelect}
                    disabled={disabled}
                    onInputChange={handleSearch}
                    noOptionsText={noOptionsText(
                        searchTerm,
                        "Start typing to search for an address...",
                        "Continue typing to search for an address...",
                        "No address found. Please try another search or enter manually"
                    )}
                    renderOption={(props, option, { inputValue }) => {
                        const matches = match(String(option.Text), inputValue, {
                            insideWords: true,
                        })
                        const parts = parse(String(option.Text), matches)
                        return (
                            // eslint-disable-next-line react/jsx-props-no-spreading
                            <li {...props}>
                                <div>
                                    {parts.map((part, index) => (
                                        <span
                                            key={
                                                // eslint-disable-next-line react/no-array-index-key
                                                part.text +
                                                part.highlight +
                                                index
                                            }
                                            style={{
                                                fontWeight: part.highlight
                                                    ? 700
                                                    : 400,
                                            }}
                                        >
                                            {part.text}
                                        </span>
                                    ))}
                                </div>
                            </li>
                        )
                    }}
                    renderInput={(params) => {
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        const { InputLabelProps, InputProps, ...otherParams } =
                            params
                        return (
                            <>
                                <TextField
                                    // eslint-disable-next-line react/jsx-props-no-spreading
                                    {...otherParams}
                                    required
                                    label={label}
                                    placeholder="Start typing to search for address line 1..."
                                    InputProps={{
                                        ...InputProps,
                                        sx: {
                                            borderRadius:
                                                ITEM_BORDER_MULTIPLIER,
                                        },
                                        endAdornment: (
                                            <>
                                                {searchAddressLoading ? (
                                                    <CircularProgress
                                                        color="inherit"
                                                        size={20}
                                                    />
                                                ) : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                                <Typography
                                    fontSize={10}
                                    marginTop={0.5}
                                    color="text.secondary"
                                >
                                    Note: you cannot search by postcode using
                                    this box. You must search on address line 1.
                                    If you have any issues, click enter
                                    manually!
                                </Typography>
                            </>
                        )
                    }}
                />
                {!isMob && (
                    <HelpIcon
                        tooltip={`${
                            disabled ? TT_AMENDMENT_MODE_DISABLED : ""
                        }${TT_ADDRESS_SEARCH}`}
                    />
                )}
            </Stack>
            {showButton && (
                <AppButton
                    onClick={displayAddressFields}
                    disabled={disabled}
                    variant="outlined"
                    color="primary"
                    endIcon={<Edit />}
                    sx={{
                        width: "auto",
                        alignSelf: "flex-start",
                    }}
                >
                    Enter address manually
                </AppButton>
            )}
            <Collapse in={hideAddressFields}>
                <Stack spacing={APPLICATION_SPACING} marginTop={0.5}>
                    <ValidatedTextField
                        control={control}
                        disabled={disabled}
                        label="Address"
                        registerName={ADDRESS_KW}
                        required
                        placeholder="Address"
                        multiline={false}
                        loading={addressLoading}
                        tooltip={`${
                            disabled ? TT_AMENDMENT_MODE_DISABLED : ""
                        }${TT_ADDRESS_SEARCH_MANUAL}`}
                    />
                    <ValidatedTextField
                        control={control}
                        disabled={disabled}
                        label="Post Code"
                        registerName={POSTCODE_KW}
                        required
                        placeholder="Post Code"
                        multiline={false}
                        loading={addressLoading}
                        tooltip={`${
                            disabled ? TT_AMENDMENT_MODE_DISABLED : ""
                        }${TT_POSTCODE_INPUT}`}
                        customRules={
                            worldwide
                                ? undefined
                                : {
                                      pattern: {
                                          value: UK_POSTCODE_REGEX,
                                          message: "Invalid postcode",
                                      },
                                  }
                        }
                    />
                </Stack>
            </Collapse>
        </>
    )
}

AddressForm.defaultProps = {
    worldwide: false,
    disabled: false,
}

export default AddressForm
